import React, { useRef, useState } from 'react';
import Button from '../ui/Button';
import { newKey } from '../../utils/newKey';
import type { DoorGuest, WalkInInput } from '../../types/Door';

interface WalkInFormProps {
  /** Resolves with the added guest, or null when the server refused (the screen shows why). */
  onSubmit: (input: WalkInInput) => Promise<DoorGuest | null>;
  onClose: () => void;
  /** The last refusal, in plain words (for example "already on the list"). */
  error: string | null;
}

/**
 * Adds a guest at the door. One idempotency key per opening of the form: a double tap, or a retry after a dropped
 * connection, sends the same key and so never makes a second guest.
 */
const WalkInForm: React.FC<WalkInFormProps> = ({ onSubmit, onClose, error }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !name.trim()) return;
    setBusy(true);
    const added = await onSubmit({
      name: name.trim(),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(email.trim() ? { email: email.trim() } : {}),
      idempotency_key: key.current,
    });
    setBusy(false);
    if (added) onClose();
  };

  return (
    <form className="door-walkin" onSubmit={submit} aria-label="Add a walk-in guest">
      <h3 className="door-walkin__title">Add a walk-in</h3>
      <label className="door-field">
        <span>Name</span>
        <input className="door-input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} autoFocus autoComplete="off" />
      </label>
      <label className="door-field">
        <span>Phone (optional)</span>
        <input className="door-input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} autoComplete="off" />
      </label>
      <label className="door-field">
        <span>E-mail (optional)</span>
        <input className="door-input" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} autoComplete="off" />
      </label>
      {error && <p className="door-walkin__error" role="alert">{error}</p>}
      <div className="door-walkin__actions">
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!name.trim()}>Add and check in</Button>
        <Button type="button" variant="secondary" size="lg" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
};

export default WalkInForm;
